import React, { useCallback, useMemo } from "react";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterTimeRange,
} from "components/commons";
import {
  locationWithTransactionFilterResponse,
  locationWithTransactionFilterRequest,
  staffWithTransactionFilterRequest,
  staffWithTransactionFilterResponse,
  deviceWithTransactionFilterResponse,
  deviceWithTransactionFilterRequest,
} from "mappers";
import { mapObject, mapObjectsToSelect } from "services";
import lang from "translations";
import { useApi, useMount } from "hooks";
import { searchShiftDevices, searchShiftLocations, searchShiftStaffs } from "apis";

const ShiftFilter = ({ filterState, requestState, modifyFilter, applyFilter, clearFilter }) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchShiftLocations,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
  });

  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchShiftStaffs,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
  });

  const {
    request: searchDeviceRequest,
    loading: loadingDevice,
    mappedData: devices,
  } = useApi({
    api: searchShiftDevices,
    isArray: true,
    mapper: deviceWithTransactionFilterResponse,
  });

  useMount(() => {
    fetchLocationRequest(requestState);
    fetchStaffRequest(requestState);
    fetchDeviceRequest(requestState);
  });

  const fetchLocationRequest = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationWithTransactionFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchStaffRequest = useCallback(
    (requestState) => {
      searchStaffRequest(mapObject({ ...requestState }, staffWithTransactionFilterRequest));
    },
    [searchStaffRequest]
  );

  const fetchDeviceRequest = useCallback(
    (requestState) => {
      searchDeviceRequest(mapObject({ ...requestState }, deviceWithTransactionFilterRequest));
    },
    [searchDeviceRequest]
  );

  const applyFilterCb = useCallback(
    async (searchKey) => {
      await applyFilter(searchKey);
    },
    [applyFilter]
  );

  const clearFilterCb = useCallback(() => {
    clearFilter();
  }, [clearFilter]);

  const changeDateRangeCb = useCallback(
    (value) => {
      const { requestState } = modifyFilter("dateRange", { value });
      fetchLocationRequest(requestState);
      fetchStaffRequest(requestState);
      fetchDeviceRequest(requestState);
    },
    [fetchLocationRequest, fetchStaffRequest, fetchDeviceRequest, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  const staffOptions = useMemo(() => {
    return mapObjectsToSelect(staffs, { textKey: "name" });
  }, [staffs]);

  const deviceOptions = useMemo(() => {
    return mapObjectsToSelect(devices, { textKey: "name" });
  }, [devices]);

  return (
    <FilterBar
      placeholder={lang.searchReportsByShiftId}
      className="mb-lg"
      filterState={filterState}
      onClear={clearFilterCb}
      onApply={applyFilterCb}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dateRange}
        maxDays={90}
        value={filterState.dateRange}
        onChange={changeDateRangeCb}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.timeRange}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterMultiSelect
        id="staffs"
        label={lang.staff}
        allLabel={lang.allStaff}
        emptyLabel={lang.noAvailableStaff}
        disableWhenEmpty={false}
        loading={loadingStaff}
        options={staffOptions}
        value={filterState.staffs}
        onChange={(value) => modifyFilter("staffs", { value })}
      />
      <FilterMultiSelect
        id="devices"
        label={lang.device}
        allLabel={lang.allDevices}
        emptyLabel={lang.noDeviceAvailable}
        disableWhenEmpty={false}
        loading={loadingDevice}
        options={deviceOptions}
        value={filterState.devices}
        onChange={(value) => modifyFilter("devices", { value })}
      />
    </FilterBar>
  );
};

export default ShiftFilter;
